import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

const REGISTRATION_FORM_KEY = 'registration-form';

interface RegistrationFormResponse {
    min_age_seconds: number;
    token: string;
}

export interface RegistrationFormToken {

    /** Seconds the backend requires between issuing and accepting the token. */
    minAgeSeconds: number;
    token: string;
}

export interface RegistrationPayload {
    city: string;
    company_name: string;
    country_code: string;
    email: string;
    first_name: string;
    form_token: string;
    last_name: string;
    locale: string;
    postal_code: string;
    street: string;
    telephone?: string | null;
    vat_number?: string | null;
    website?: string;
}

export interface RegistrationErrorBody {
    code?: number;
    errors?: Record<string, string[]>;
    message?: string;
}

/**
 * Signed timestamp the register form must echo back. Never cached: every
 * mount gets a fresh token, and `useRegister` refetches it when the backend
 * reports the one it got was too fresh, too old or tampered with.
 */
export function useRegistrationForm(enabled = true) {
    const { data, isPending, refetch } = useQuery<RegistrationFormResponse>({
        queryKey: [
            REGISTRATION_FORM_KEY,
        ],
        queryFn: () => {
            return axios.get<RegistrationFormResponse>('/api/register/form').then(({ data: payload }) => {
                return payload;
            });
        },
        enabled,
        gcTime: 0,
        staleTime: 0,
        retry: 1,
    });

    const toFormToken = (payload?: RegistrationFormResponse): RegistrationFormToken | null => {
        return payload ? { minAgeSeconds: payload.min_age_seconds, token: payload.token } : null;
    };

    return {
        formToken: toFormToken(data),
        isPending,
        refetch: async () => {
            return toFormToken((await refetch()).data);
        },
    };
}

export function isFormTokenError(error: unknown): boolean {
    const body = (error as AxiosError<RegistrationErrorBody>)?.response?.data;

    return !!body?.errors?.form_token;
}

export function useRegister() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: RegistrationPayload) => {
            return axios.post('/api/register', payload).then(({ data }) => {
                return data;
            });
        },
        onSettled: () => {
            queryClient.removeQueries({ queryKey: [
                REGISTRATION_FORM_KEY,
            ] });
        },
    });
}
